// src/components/crm/ProgressScore.js
import React, { useState, useEffect } from 'react';

const ProgressScore = ({ 
  score = 0, 
  showBadge = false, 
  showTooltip = true, 
  size = 'medium',
  animate = true 
}) => {
  const [animatedWidth, setAnimatedWidth] = useState(0);
  const [showTooltipState, setShowTooltipState] = useState(false);
  
  const normalizedScore = Math.min(Math.max(score, 0), 100);
  
  // Nouveaux seuils alignés avec le backend :
  // ≥65% → Chaud (vert)
  // 30–64% → Moyen (orange)
  // <30% → Froid/Perdu (rouge)
const getScoreColor = () => {
  if (normalizedScore >= 76) return '#10B981'; // vert
  if (normalizedScore >= 40) return '#F59E0B'; // orange
  if (normalizedScore >= 5)  return '#64748B'; // gris/bleu
  return '#EF4444'; // rouge
};

const getScoreLevel = () => {
  if (normalizedScore >= 76) return { label: 'chaud', icon: '🔥' };
  if (normalizedScore >= 40) return { label: 'moyen', icon: '🟡' };
  if (normalizedScore >= 5)  return { label: 'froid',  icon: '❄️' };
  return { label: 'Perdu', icon: '🚫' };
};

  const getSizeClasses = () => {
    switch(size) {
      case 'small':
        return {
          container: 'progress-score-container small',
          bar: 'progress-score-bar small',
          percentage: 'progress-score-percentage small',
          badge: 'progress-score-badge small'
        };
      case 'large':
        return {
          container: 'progress-score-container large',
          bar: 'progress-score-bar large',
          percentage: 'progress-score-percentage large',
          badge: 'progress-score-badge large'
        };
      default:
        return {
          container: 'progress-score-container',
          bar: 'progress-score-bar',
          percentage: 'progress-score-percentage',
          badge: 'progress-score-badge'
        };
    }
  };
  
  const sizeClasses = getSizeClasses();
  const scoreColor = getScoreColor();
  const scoreLevel = getScoreLevel();
  
  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => {
        setAnimatedWidth(normalizedScore);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setAnimatedWidth(normalizedScore);
    }
  }, [normalizedScore, animate]);
  
  const handleMouseEnter = () => {
    if (showTooltip) setShowTooltipState(true);
  };
  
  const handleMouseLeave = () => {
    if (showTooltip) setShowTooltipState(false);
  };
  
  return React.createElement('div', {
    className: `progress-score-wrapper ${sizeClasses.container}`,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    style: { position: 'relative', display: 'inline-block', width: '100%' }
  },
    React.createElement('div', { className: 'progress-score-inner', style: { display: 'flex', alignItems: 'center', gap: '8px' } },
      React.createElement('div', { className: `progress-score-track ${sizeClasses.bar}`, style: { flex: 1 } },
        React.createElement('div', {
          className: `progress-score-fill ${sizeClasses.bar}`,
          style: {
            width: `${animatedWidth}%`,
            backgroundColor: scoreColor,
            transition: animate ? 'width 0.6s ease-out' : 'none',
            borderRadius: 'inherit',
            height: '100%'
          }
        })
      ),
      React.createElement('span', { className: sizeClasses.percentage, style: { color: scoreColor, fontWeight: '600', minWidth: '45px', textAlign: 'right' } },
        `${Math.round(normalizedScore)}%`
      )
    ),
    showBadge && React.createElement('div', { className: sizeClasses.badge, style: { marginTop: '4px' } },
      React.createElement('span', { style: { marginRight: '4px' } }, scoreLevel.icon),
      React.createElement('span', null, scoreLevel.label)
    ),
    showTooltip && showTooltipState && React.createElement('div', {
      className: 'progress-score-tooltip',
      style: {
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: '8px',
        background: '#1e293b',
        color: '#fff',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        whiteSpace: 'nowrap',
        zIndex: 1000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }
    },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '4px' } },
        React.createElement('i', { className: 'fa-solid fa-chart-line', style: { fontSize: '10px' } }),
        React.createElement('span', null, `Probabilité de conversion: ${Math.round(normalizedScore)}%`),
        React.createElement('span', { style: { marginLeft: '4px', opacity: 0.7 } }, scoreLevel.icon)
      ),
      React.createElement('div', {
        style: {
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '6px solid #1e293b'
        }
      })
    )
  );
};

export default ProgressScore;